/**
 * EmergencyCountersignPanel — the second signature on break-glass access.
 *
 * Shown to any administrator, not only custodians: the whole point of an
 * emergency route is that a custodian may be the person who cannot be reached.
 * It still takes two people, and never the same one twice.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Siren } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = body.detail;
    throw new Error(typeof d === 'string' ? d : (d?.reason || `Request failed (${res.status})`));
  }
  return body;
};

const minutesLeft = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
};

export const EmergencyCountersignPanel = () => {
  const qc = useQueryClient();
  const { data, isError } = useQuery({
    queryKey: ['sensitive-emergencies'],
    queryFn: () => api('/finance/sensitive/emergency/pending'),
    refetchInterval: 30000,
    retry: false,
  });

  const rows = data?.emergencies || [];
  if (isError) {
    // Silence here would hide a crisis, so say so rather than show nothing.
    return (
      <p className="mb-4 text-xs text-rose-600" data-testid="emergency-panel-error">
        Emergency access requests could not be checked just now.
      </p>
    );
  }
  if (rows.length === 0) return null;
  const countersign = async (id) => {
    if (!window.confirm('Countersign this emergency access? It opens the figure '
      + 'for one hour and every custodian is told.')) return;
    try {
      const res = await api(`/finance/sensitive/emergency/${id}/countersign`, { method: 'POST' });
      toast.success(`Countersigned — access expires ${new Date(res.expires_at).toLocaleTimeString()}`);
      qc.invalidateQueries({ queryKey: ['sensitive-emergencies'] });
      qc.invalidateQueries({ queryKey: ['sensitive-grants'] });
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card className="mb-6 border-rose-500/40" data-testid="emergency-countersign-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-rose-600 text-base">
          <Siren className="h-5 w-5" /> Emergency access awaiting a second signature
        </CardTitle>
        <CardDescription>
          Granted for {data?.grant_minutes || 60} minutes only, never renewable, and
          reported to every custodian. If this is not a genuine emergency, leave it
          to lapse.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-start justify-between gap-3 border rounded-lg px-3 py-2"
               data-testid={`emergency-row-${r.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium">{r.resource_label}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {r.user_email || r.user_id} · lapses in {minutesLeft(r.countersign_deadline)} min
              </p>
              <p className="text-sm mt-1">{r.reason}</p>
            </div>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 shrink-0"
                    disabled={!r.can_countersign}
                    title={r.can_countersign ? '' : 'You raised this one — it needs someone else'}
                    onClick={() => countersign(r.id)}
                    data-testid={`countersign-btn-${r.id}`}>
              Countersign
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default EmergencyCountersignPanel;
